import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  Clock,
  Calendar,
  ArrowRight,
  X,
  Minus,
  ChevronDown,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { AllAlertsModal } from "./AllAlertsModal";
import { getGlobalStats, isPendingApproval } from "@/services/obraStats";
import { listObras } from "@/lib/obraCache";

interface AlertItem {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  time: string;
  severity: "high" | "medium" | "low";
}

// Alertas derivados dos dados reais: RDOs pendentes de aprovação e obras
// ativas sem relatório recente.
async function buildAlerts(): Promise<AlertItem[]> {
  const [stats, obras] = [await getGlobalStats(), listObras()];
  const alerts: AlertItem[] = [];

  const pendentes = stats.rdos.filter(isPendingApproval);
  if (pendentes.length > 0) {
    alerts.push({
      id: "pendentes-aprovacao",
      icon: AlertTriangle,
      title: `${pendentes.length} RDO(s) pendente(s) de aprovação`,
      description: `Mais recente: ${pendentes[0].date.split("-").reverse().join("/")}`,
      time: "agora",
      severity: pendentes.length > 3 ? "high" : "medium",
    });
  }

  const hoje = new Date();
  for (const obra of obras) {
    if (obra.statusObra === "concluido" || obra.statusObra === "cancelado") continue;
    const ultimo = stats.rdos.find((r) => r.obraId === obra.id);
    if (!ultimo) {
      alerts.push({
        id: `sem-rdo-${obra.id}`,
        icon: Calendar,
        title: `${obra.nomeObra || "Obra"} sem nenhum relatório`,
        description: "Nenhum RDO foi preenchido para esta obra ainda.",
        time: "",
        severity: "low",
      });
      continue;
    }
    const dias = Math.floor(
      (hoje.getTime() - new Date(`${ultimo.date}T12:00:00`).getTime()) / 86400000
    );
    if (dias >= 2) {
      alerts.push({
        id: `atraso-rdo-${obra.id}`,
        icon: Clock,
        title: `${obra.nomeObra || "Obra"} sem relatório há ${dias} dias`,
        description: `Último RDO em ${ultimo.date.split("-").reverse().join("/")}.`,
        time: "",
        severity: dias >= 7 ? "high" : "medium",
      });
    }
  }

  return alerts.slice(0, 6);
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'high':
      return 'bg-destructive/10 text-destructive border-destructive/20';
    case 'medium':
      return 'bg-warning/10 text-warning border-warning/20';
    case 'low':
      return 'bg-muted text-muted-foreground border-border';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
};

const getIconColor = (severity: string) => {
  switch (severity) {
    case 'high':
      return 'text-destructive';
    case 'medium':
      return 'text-warning';
    case 'low':
      return 'text-muted-foreground';
    default:
      return 'text-muted-foreground';
  }
};

interface RecentAlertsProps {
  isVisible?: boolean;
  onVisibilityChange?: (visible: boolean) => void;
}

export const RecentAlerts = ({ isVisible = true, onVisibilityChange }: RecentAlertsProps) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isAllAlertsModalOpen, setIsAllAlertsModalOpen] = useState(false);

  // Alertas reais derivados dos RDOs/obras
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  useEffect(() => {
    let active = true;
    buildAlerts()
      .then((a) => { if (active) setAlerts(a); })
      .catch((e) => console.error("RecentAlerts:", e));
    return () => { active = false; };
  }, []);

  if (!isVisible) return null;

  return (
    <>
      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-foreground">Alertas Recentes</CardTitle>
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setIsAllAlertsModalOpen(true)}
            >
              Ver todos
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-11 w-11 p-0 sm:h-6 sm:w-6"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <ChevronDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-11 w-11 p-0 sm:h-6 sm:w-6"
            onClick={() => onVisibilityChange?.(false)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      {!isMinimized && (
        <CardContent className="space-y-4">
        {alerts.length === 0 && (
          <div className="py-6 flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
            <CheckCircle2 className="h-8 w-8 opacity-40" />
            <span>Tudo em dia — nenhum alerta no momento.</span>
          </div>
        )}
        {alerts.map((alert) => (
          <div 
            key={alert.id}
            className="group relative flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
          >
            <Button 
              variant="ghost" 
              size="sm" 
              className="absolute top-2 right-2 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-destructive/10"
              onClick={(e) => {
                e.stopPropagation();
                // Handle close action
              }}
            >
              <X className="h-3 w-3 text-muted-foreground hover:text-destructive" />
            </Button>
            
            <div className={`p-2 rounded-full ${getSeverityColor(alert.severity)}`}>
              <alert.icon className={`h-4 w-4 ${getIconColor(alert.severity)}`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-foreground text-sm mb-1">
                {alert.title}
              </h4>
              <p className="text-xs text-muted-foreground mb-2">
                {alert.description}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {alert.time}
                </span>
                <Badge 
                  variant="outline"
                  className={`text-xs ${getSeverityColor(alert.severity)}`}
                >
                  {alert.severity === 'high' ? 'Alta' : 
                   alert.severity === 'medium' ? 'Média' : 'Baixa'}
                </Badge>
              </div>
            </div>
          </div>
        ))}
        </CardContent>
      )}
      </Card>

      <AllAlertsModal
        alerts={alerts}
        open={isAllAlertsModalOpen}
        onOpenChange={setIsAllAlertsModalOpen}
      />
    </>
  );
};